import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  catalogBanks,
  emptyAddress,
  emptyBankAccount,
  paymentMethods,
  policyholderFile,
  policyholders,
  type Address,
  type BankAccount,
  type CreditProfile,
  type Policyholder,
  type PolicyholderInput,
} from '@/shared/api/resources';
import type { Money } from '@/shared/api/types';
import { useAuth } from '@/shared/auth/useAuth';
import {
  formatCivilDate,
  formatCnae,
  formatCpfCnpj,
  formatDate,
  formatDateTime,
  formatMoney,
  formatRate,
  moneyInputError,
  moneyToInput,
  parseMoney,
} from '@/shared/lib/format';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { AddressFields } from '@/shared/ui/AddressFields';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { ConfirmDialog, Modal } from '@/shared/ui/Modal';
import { MoneyField } from '@/shared/ui/MoneyField';
import { Select } from '@/shared/ui/Select';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { FinancialBankAccountFields } from './BankAccountFields';
import { ListasDoCadastro } from './ListasDoCadastro';
import { MasterConsultationButton } from '../../creditConsumption/MasterConsultationButton';
import { fieldErrorOf } from '../../shared/fieldError';
import { PolicyholderForm } from '../PolicyholderForm';
import {
  classesDeRisco,
  portesDeCredito,
  rotuloDoTipoDePessoa,
  rotuloSituacaoDeCredito,
  situacoesEspeciais,
} from '../rotulos';
import { AnaliseAutomaticaDoTomador } from './AnaliseAutomaticaDoTomador';
import { textoDaOrigem } from './origemDoCampo';
import { BotaoDeReprocessamento } from './RegrasEHistorico';
import { Abas, Bloco, Info, InfoGrid, Secao } from './shared';

type Aba = 'cadastrais' | 'complementares' | 'credito' | 'bancarios' | 'observacoes';

const instrumentos: Record<string, string> = {
  primeira_emissao: 'Primeira emissao',
  ccg: 'CCG',
  carta_nomeacao: 'Carta de nomeacao',
};

function dinheiro(valor: Money | null | undefined): string {
  return valor ? formatMoney(valor) : '';
}

function simNao(valor: boolean | null | undefined): string {
  if (valor === true) return 'Sim';
  if (valor === false) return 'Nao';
  return '';
}

/**
 * Secao "Cadastro Completo", com as cinco abas do OnPoint: Dados Cadastrais,
 * Dados Complementares, Analise de Credito, Dados Bancarios e Observacoes.
 *
 * A corretora ve, como no OnPoint, Dados Cadastrais (so a identificacao), Dados
 * Complementares e Dados Bancarios. As demais pedem a leitura da ficha interna.
 */
export function CadastroCompleto({ tomador }: { tomador: Policyholder }) {
  const { can } = useAuth();
  const fichaInterna = can('tomadores.ficha.view');
  const [aba, setAba] = useState<Aba>('cadastrais');

  const abas: { chave: Aba; titulo: string }[] = [
    { chave: 'cadastrais', titulo: 'Dados Cadastrais' },
    { chave: 'complementares', titulo: 'Dados Complementares' },
    ...(fichaInterna
      ? [{ chave: 'credito' as Aba, titulo: 'Analise de Credito' }]
      : []),
    { chave: 'bancarios', titulo: 'Dados Bancarios' },
    ...(fichaInterna ? [{ chave: 'observacoes' as Aba, titulo: 'Observacoes' }] : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      <Abas abas={abas} ativa={aba} onChange={setAba} rotulo="Cadastro Completo" />
      {aba === 'cadastrais' ? <DadosCadastrais tomador={tomador} /> : null}
      {aba === 'complementares' ? <DadosComplementares tomador={tomador} /> : null}
      {aba === 'credito' ? <AnaliseDeCredito tomador={tomador} /> : null}
      {aba === 'bancarios' ? <DadosBancarios tomador={tomador} /> : null}
      {aba === 'observacoes' ? <Observacoes tomador={tomador} /> : null}
    </div>
  );
}

function useInvalidarTomador(id: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['policyholders'] });
    void queryClient.invalidateQueries({ queryKey: ['policyholder', id] });
  };
}

// --- Dados Cadastrais ------------------------------------------------------------

function DadosCadastrais({ tomador }: { tomador: Policyholder }) {
  const { can, session } = useAuth();
  const daCorretora = session?.broker_scoped === true;
  const toast = useToast();
  const invalidar = useInvalidarTomador(tomador.id);
  const fichaInterna = can('tomadores.ficha.view');
  const podeEditar = can('tomadores.tomador.update');
  const [editando, setEditando] = useState(false);

  const perfil = useQuery({
    queryKey: ['policyholder', tomador.id, 'credit-profile'],
    queryFn: ({ signal }) => policyholderFile.creditProfile(tomador.id, signal),
    enabled: fichaInterna,
  });

  const atualizar = useMutation({
    mutationFn: (input: PolicyholderInput) => policyholders.update(tomador.id, input),
    onSuccess: () => {
      toast.success('Cadastro atualizado.');
      setEditando(false);
      invalidar();
    },
  });

  const dispensa = useMutation({
    mutationFn: (ignorar: boolean) => policyholders.setCcgWaiver(tomador.id, ignorar),
    onSuccess: () => {
      toast.success('Assinatura de CCG atualizada.');
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  const restritivo = useMutation({
    mutationFn: (bloqueado: boolean) =>
      policyholders.setRestrictiveBlock(tomador.id, bloqueado),
    onSuccess: () => {
      toast.success('Bloqueio por restritivos atualizado.');
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  const p = perfil.data;

  return (
    <Secao
      titulo="Dados Cadastrais"
      descricao="Identificacao, corretagem, empresa, financeiro, risco e restritivos do tomador."
      acao={
        <div className="flex flex-wrap gap-2">
          {fichaInterna ? <BotaoDeReprocessamento tomador={tomador} /> : null}
          {podeEditar ? (
            <Button size="sm" onClick={() => setEditando(true)}>
              Editar cadastro
            </Button>
          ) : null}
        </div>
      }
      erro={perfil.error}
    >
      {daCorretora ? (
        // Aviso de nomeacao do OnPoint (TOM-023). A corretora so alcanca o tomador
        // da propria corretora; a validade e mostrada, e o efeito da nomeacao vencida
        // esta pendente de regra (CAD-016).
        <Alert tone="info" title="Tomador nomeado para sua corretora">
          {tomador.nomination_valid_until
            ? `Nomeacao valida ate ${formatCivilDate(tomador.nomination_valid_until)}.`
            : 'Sem validade de nomeacao registrada.'}
        </Alert>
      ) : null}
      <Bloco titulo="Identificacao">
        <InfoGrid>
          <Info label="Razao Social" value={tomador.legal_name} />
          <Info label="CPF/CNPJ" value={formatCpfCnpj(tomador.document)} />
          <Info
            label="Tipo de pessoa"
            value={rotuloDoTipoDePessoa(tomador.document_kind)}
          />
          <Info label="Data de registro" value={formatDate(tomador.created_at)} />
          <Info
            label="Data de validade cadastral"
            value={formatCivilDate(tomador.registration_valid_until)}
          />
          <Info
            label="Data recadastro"
            value={formatCivilDate(tomador.reregistered_on)}
          />
          <Info label="Nome fantasia" value={tomador.trade_name} />
          <Info label="Inscricao estadual" value={tomador.state_registration} />
          {fichaInterna ? (
            <>
              <Info label="Direito privado" value={simNao(tomador.is_private)} />
              <Info
                label="Origem do cadastro"
                value={
                  tomador.import_source
                    ? `Carga: ${tomador.import_source}`
                    : 'Cadastrado no SeguraCore'
                }
              />
              {tomador.document_invalid ? (
                <Info
                  label="Documento"
                  value="Marcado como invalido pela carga de dados"
                  hint="O digito verificador nao confere. Ao trocar o documento, ele e conferido por inteiro."
                />
              ) : null}
            </>
          ) : null}
        </InfoGrid>
      </Bloco>

      {fichaInterna ? (
        <>
          <Bloco titulo="Corretagem">
            <InfoGrid>
              <Info
                label="Comissao"
                value={
                  tomador.commission_bps === null ||
                  tomador.commission_bps === undefined
                    ? ''
                    : formatRate(tomador.commission_bps)
                }
              />
              <Info label="Corretor Nomeado" value={tomador.broker?.label ?? ''} />
              <Info
                label="Data de validade da nomeacao"
                value={formatCivilDate(tomador.nomination_valid_until)}
              />
              <Info
                label="Instrumento da nomeacao"
                value={instrumentos[tomador.nomination_instrument ?? ''] ?? ''}
              />
              <Info label="Comercial responsavel" value={tomador.commercial_owner} />
            </InfoGrid>
          </Bloco>

          <Bloco titulo="Empresa">
            <InfoGrid>
              <Info
                label="Data de fundacao"
                value={formatCivilDate(tomador.founded_on)}
              />
              <Info label="Obs. Fundacao" value={tomador.foundation_note} />
              <Info
                label="Situacao"
                value={tomador.registration_situation?.toUpperCase()}
              />
              <Info label="CNAE" value={formatCnae(tomador.cnae)} />
              <Info label="Descricao do CNAE" value={tomador.cnae_description} />
              <Info
                label="CNAEs secundarios"
                value={(tomador.secondary_cnaes ?? []).map(formatCnae).join(', ')}
              />
              <Info label="Ramo de atuacao" value={tomador.branch_activity} />
              <Info
                label="Ramo/CNAE Restrito?"
                value={simNao(tomador.branch_activity_restricted)}
              />
              <Info label="Natureza juridica" value={tomador.legal_nature} />
            </InfoGrid>
          </Bloco>

          <Bloco titulo="Financeiro">
            <InfoGrid>
              <Info
                label="Fat. Presumido Anual (R$)"
                value={dinheiro(p?.presumed_revenue)}
                nota={textoDaOrigem(p, 'presumed_revenue')}
              />
              <Info
                label="Capital Social (R$)"
                value={formatMoney(tomador.share_capital)}
              />
              <Info
                label="Capacidade de pagamento (R$)"
                value={dinheiro(p?.payment_capacity)}
              />
              <Info
                label="Porte"
                value={rotuloDoPorte(p?.credit_size)}
                nota={textoDaOrigem(p, 'credit_size')}
              />
              <Info label="Pagamento maximo (R$)" value={dinheiro(p?.max_payment)} />
              <Info
                label="Limite sugerido pelo bureau (R$)"
                value={dinheiro(p?.bureau_limit)}
              />
              <Info label="Limite maximo Wiz (R$)" value={dinheiro(p?.wiz_max_limit)} />
              <Info label="Limite maximo CP (R$)" value={dinheiro(p?.cp_max_limit)} />
              <Info label="Bonus Wiz (R$)" value={dinheiro(p?.wiz_bonus)} />
              <Info
                label="Percentual do faturamento anual"
                value={
                  tomador.annual_billing_percentage_bps === null ||
                  tomador.annual_billing_percentage_bps === undefined
                    ? ''
                    : formatRate(tomador.annual_billing_percentage_bps)
                }
              />
            </InfoGrid>
          </Bloco>

          <Bloco titulo="Risco">
            <InfoGrid>
              <Info
                label="Classe de Risco Financeiro"
                value={p?.financial_risk_class}
                nota={textoDaOrigem(p, 'financial_risk_class')}
              />
              <Info
                label="Score Final"
                value={p?.final_score ?? ''}
                nota={textoDaOrigem(p, 'final_score')}
              />
              <Info label="Score" value={p?.score ?? ''} />
              <Info label="Risco Score" value={p?.risk_score} />
              <Info
                label="Status de credito"
                value={rotuloSituacaoDeCredito(tomador.credit_status)}
              />
              {tomador.credit_status === 'reprovado' ? (
                <Info label="Motivo da negativa" value={tomador.credit_denial_reason} />
              ) : null}
              <Info
                label="Situacao especial"
                value={p?.special_situation}
                nota={textoDaOrigem(p, 'special_situation')}
              />
            </InfoGrid>
          </Bloco>

          <Bloco titulo="RESTRITIVOS DO TOMADOR:">
            <InfoGrid>
              <Info
                label="Debitos Pefins (R$)"
                value={dinheiro(p?.pefin)}
                nota={textoDaOrigem(p, 'pefin')}
              />
              <Info
                label="Debito PEFIN, valor da API (R$)"
                value={dinheiro(p?.pefin_debit)}
              />
              <Info
                label="Debitos Refins (R$)"
                value={dinheiro(p?.refin)}
                nota={textoDaOrigem(p, 'refin')}
              />
              <Info label="CCF" value={p?.ccf_count ?? ''} />
              <Info
                label="Protestos (R$)"
                value={dinheiro(p?.protest)}
                nota={textoDaOrigem(p, 'protest')}
              />
              <Info
                label="Dividas Vencidas (R$)"
                value={dinheiro(p?.overdue_debt)}
                nota={textoDaOrigem(p, 'overdue_debt')}
              />
              <Info
                label="Percentual PEFIN"
                value={
                  p?.pefin_percentage_bps == null
                    ? ''
                    : formatRate(p.pefin_percentage_bps)
                }
              />
              <Info
                label="Percentual de dividas"
                value={
                  p?.debit_percentage_bps == null
                    ? ''
                    : formatRate(p.debit_percentage_bps)
                }
              />
              <Info
                label="Percentual de protestos"
                value={
                  p?.protest_percentage_bps == null
                    ? ''
                    : formatRate(p.protest_percentage_bps)
                }
              />
              <Info
                label="Pontos"
                value={p?.restrictive_points ?? ''}
                nota={textoDaOrigem(p, 'restrictive_points')}
              />
              <Info
                label="Data de Consulta Restritivos"
                value={formatCivilDate(p?.restrictive_consulted_on)}
                nota={textoDaOrigem(p, 'restrictive_consulted_on')}
              />
              <Info
                label="Bureau"
                value={p?.bureau_name}
                nota={textoDaOrigem(p, 'bureau_name')}
              />
            </InfoGrid>
            <label className="flex items-center gap-2 text-sm text-ink-800">
              <input
                type="checkbox"
                className="size-4"
                checked={tomador.blocked_by_restrictive ?? false}
                disabled={!can('tomadores.restritivo.update') || restritivo.isPending}
                onChange={(event) => restritivo.mutate(event.target.checked)}
              />
              Bloqueado por restritivos
            </label>
          </Bloco>

          <Bloco titulo="Ignorar assinatura de CCG">
            <label className="flex items-center gap-2 text-sm text-ink-800">
              <input
                type="checkbox"
                role="switch"
                className="size-4"
                checked={tomador.ignore_ccg_signature ?? false}
                disabled={!can('tomadores.ccg.approve') || dispensa.isPending}
                onChange={(event) => dispensa.mutate(event.target.checked)}
              />
              Ignorar assinatura de CCG
            </label>
            <p className="text-2xs text-ink-500">
              Outros indicadores: parcelamento anual{' '}
              {simNao(tomador.enable_annual_installments)}, revisao automatica do limite{' '}
              {simNao(tomador.limit_automatic_review)}, aprovacao forcada apesar de
              lista restritiva {simNao(tomador.force_approved_dirty_list)}, classe de
              pagamento {tomador.payment_equivalent_class || 'nao informada'}, ultima
              avaliacao de sinistros{' '}
              {formatCivilDate(tomador.last_claims_evaluation_on) || 'nao informada'}.
            </p>
          </Bloco>
        </>
      ) : null}

      <Modal
        open={editando}
        onClose={() => setEditando(false)}
        title={tomador.legal_name}
        description={`Tomador ${tomador.number}`}
        size="lg"
      >
        <PolicyholderForm
          holder={tomador}
          submitting={atualizar.isPending}
          error={atualizar.error}
          onCancel={() => setEditando(false)}
          onSubmit={(input) => atualizar.mutate(input)}
        />
      </Modal>
    </Secao>
  );
}

function rotuloDoPorte(valor: string | undefined): string {
  return portesDeCredito.find((porte) => porte.value === valor)?.label ?? '';
}

// --- Dados Complementares -------------------------------------------------------

function DadosComplementares({ tomador }: { tomador: Policyholder }) {
  const { can } = useAuth();
  const toast = useToast();
  const invalidar = useInvalidarTomador(tomador.id);
  const podeEditar = can('tomadores.tomador.update');
  const [endereco, setEndereco] = useState<Address>({
    ...emptyAddress,
    city_code: '',
    ...tomador.address,
  });
  const [telefone, setTelefone] = useState(tomador.phone);
  const [email, setEmail] = useState(tomador.email);

  const salvar = useMutation({
    mutationFn: () =>
      policyholders.update(tomador.id, { address: endereco, phone: telefone, email }),
    onSuccess: () => {
      toast.success('Dados complementares salvos.');
      invalidar();
    },
  });
  const apiError = salvar.error instanceof ApiError ? salvar.error : null;

  function submit(event: FormEvent) {
    event.preventDefault();
    salvar.mutate();
  }

  return (
    <Secao
      titulo="Dados Complementares"
      descricao="Endereco e contato. A tela pede endereco e telefone, e o servidor ainda aceita o cadastro antigo sem eles."
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        {salvar.error ? (
          <Alert tone="danger" title="Nao foi possivel salvar">
            {salvar.error.message}
          </Alert>
        ) : null}
        <fieldset
          disabled={!podeEditar || salvar.isPending}
          className="flex flex-col gap-4"
        >
          <AddressFields
            value={endereco}
            onChange={setEndereco}
            required
            withCityCode
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Telefone"
              required
              placeholder="(00) 0000-0000"
              value={telefone}
              onChange={(event) => setTelefone(event.target.value)}
            />
            <Field
              label="Email"
              type="email"
              value={email}
              error={fieldErrorOf(apiError, 'email')}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
        </fieldset>
        {podeEditar ? (
          <div className="flex justify-end">
            <Button type="submit" loading={salvar.isPending}>
              Salvar
            </Button>
          </div>
        ) : null}
      </form>
      <ListasDoCadastro tomador={tomador} podeEditar={podeEditar} />
    </Secao>
  );
}

// --- Analise de Credito -----------------------------------------------------------

// Zero e valor, e nulo e "nao informado": o perfil nao confunde os dois. Protestos e dividas
// zerados pela consulta precisam aparecer, e gravar o formulario nao pode apaga-los.
function campoDeDinheiro(valor: Money | null | undefined): string {
  if (valor?.amount === 0) return '0';
  return moneyToInput(valor ?? undefined);
}

function dinheiroOuNulo(texto: string): Money | null {
  if (texto.trim() === '') return null;
  return parseMoney(texto);
}

function inteiroOuNulo(texto: string): number | null {
  if (texto.trim() === '') return null;
  const numero = Number(texto);
  return Number.isInteger(numero) ? numero : null;
}

/** O formulario em texto, com o nome de cada campo na API. */
interface Rascunho {
  reviewed_payment_capacity: string;
  actual_revenue: string;
  reviewed_size: string;
  reviewed_limit: string;
  reviewed_on: string;
  review_notes: string;
  credit_size: string;
  financial_risk_class: string;
  final_score: string;
  risk_score: string;
  presumed_revenue: string;
  payment_capacity: string;
  pefin: string;
  refin: string;
  protest: string;
  overdue_debt: string;
  ccf_count: string;
  restrictive_points: string;
  restrictive_consulted_on: string;
  special_situation: string;
  bureau_name: string;
}

const camposDeDinheiro: (keyof Rascunho)[] = [
  'reviewed_payment_capacity',
  'actual_revenue',
  'reviewed_limit',
  'presumed_revenue',
  'payment_capacity',
  'pefin',
  'refin',
  'protest',
  'overdue_debt',
];

function rascunhoDoPerfil(perfil: CreditProfile): Rascunho {
  return {
    reviewed_payment_capacity: campoDeDinheiro(perfil.reviewed_payment_capacity),
    actual_revenue: campoDeDinheiro(perfil.actual_revenue),
    reviewed_size: perfil.reviewed_size,
    reviewed_limit: campoDeDinheiro(perfil.reviewed_limit),
    reviewed_on: perfil.reviewed_on ?? '',
    review_notes: perfil.review_notes,
    credit_size: perfil.credit_size,
    financial_risk_class: perfil.financial_risk_class,
    final_score: perfil.final_score?.toString() ?? '',
    risk_score: perfil.risk_score,
    presumed_revenue: campoDeDinheiro(perfil.presumed_revenue),
    payment_capacity: campoDeDinheiro(perfil.payment_capacity),
    pefin: campoDeDinheiro(perfil.pefin),
    refin: campoDeDinheiro(perfil.refin),
    protest: campoDeDinheiro(perfil.protest),
    overdue_debt: campoDeDinheiro(perfil.overdue_debt),
    ccf_count: perfil.ccf_count?.toString() ?? '',
    restrictive_points: perfil.restrictive_points?.toString() ?? '',
    restrictive_consulted_on: perfil.restrictive_consulted_on ?? '',
    special_situation: perfil.special_situation,
    bureau_name: perfil.bureau_name,
  };
}

function AnaliseDeCredito({ tomador }: { tomador: Policyholder }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const podeCredito = can('tomadores.credito.update');
  const podeConsultar = can('credito.consulta.create');
  const [confirmando, setConfirmando] = useState(false);

  const perfil = useQuery({
    queryKey: ['policyholder', tomador.id, 'credit-profile'],
    queryFn: ({ signal }) => policyholderFile.creditProfile(tomador.id, signal),
  });
  const consultas = useQuery({
    queryKey: ['policyholder', tomador.id, 'consultations'],
    queryFn: ({ signal }) => policyholderFile.consultations(tomador.id, signal),
    enabled: can('credito.motor.view'),
  });

  const consultar = useMutation({
    mutationFn: () => policyholderFile.requestConsultation(tomador.id, true),
    onSuccess: (consulta) => {
      toast.success(
        consulta.reused
          ? consulta.reanalyzed
            ? 'Consulta reaproveitada, sem nova cobranca. As regras foram reavaliadas agora.'
            : 'Consulta valida reaproveitada, sem nova cobranca.'
          : consulta.simulated
            ? 'Consulta registrada. O bureau configurado e ficticio: o resultado e SIMULADO.'
            : 'Consulta registrada. O retorno e as regras ja estao na tela.',
      );
      setConfirmando(false);
      void queryClient.invalidateQueries({ queryKey: ['policyholder', tomador.id] });
    },
    onError: (erro: Error) => {
      setConfirmando(false);
      toast.error(erro.message);
    },
  });

  const ultima = consultas.data?.[0];
  const masterValida = consultas.data?.find((c) => c.stage === 'master' && c.valid);

  return (
    <Secao
      titulo="Analise de Credito"
      descricao="Capacidade de pagamento, faturamento real, porte, limite revisado e observacoes da analise."
      carregando={perfil.isPending}
      erro={perfil.error}
      acao={
        podeConsultar ? (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={() => setConfirmando(true)}>
              Consultar Restritivos
            </Button>
            <MasterConsultationButton
              policyholderId={tomador.id}
              masterValida={masterValida}
            />
          </div>
        ) : null
      }
    >
      {ultima ? (
        <Alert
          tone={ultima.simulated ? 'warning' : 'info'}
          title="Ultima consulta ao bureau"
        >
          {formatDateTime(ultima.consulted_at)} por{' '}
          {ultima.requested_label || 'sistema'}, situacao {ultima.status}
          {ultima.simulated ? ', SIMULADA (bureau ficticio)' : ''}. Os dados estao
          abaixo e em Historico de Consultas.
        </Alert>
      ) : (
        <Alert tone="info" title="Ops!">
          Nenhuma consulta realizada ainda. Clique em Consultar Restritivos para buscar
          os dados do bureau.
        </Alert>
      )}
      <AnaliseAutomaticaDoTomador tomadorId={tomador.id} documento={tomador.document} />
      {perfil.data ? (
        <FormularioDoPerfil
          key={tomador.id}
          tomadorId={tomador.id}
          perfil={perfil.data}
          podeEditar={podeCredito}
        />
      ) : null}
      <ConfirmDialog
        open={confirmando}
        onClose={() => setConfirmando(false)}
        title="Consultar restritivos"
        message="A consulta ao bureau pode ser cobrada pelo fornecedor. Uma consulta valida recente do mesmo CNPJ e reaproveitada, sem nova cobranca."
        confirmLabel="Consultar"
        loading={consultar.isPending}
        onConfirm={() => consultar.mutate()}
      />
    </Secao>
  );
}

function FormularioDoPerfil({
  tomadorId,
  perfil,
  podeEditar,
}: {
  tomadorId: string;
  perfil: CreditProfile;
  podeEditar: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  // O que a pessoa mudou desde a ultima gravacao. O resto vem do perfil gravado: a consulta
  // preenche o formulario sem atualizar a pagina, e nunca apaga o que esta sendo digitado.
  const [editados, setEditados] = useState<Partial<Rascunho>>({});
  const valores: Rascunho = { ...rascunhoDoPerfil(perfil), ...editados };

  function aoDigitar(campo: keyof Rascunho) {
    return (valor: string) => {
      setEditados((atuais) => ({ ...atuais, [campo]: valor }));
    };
  }

  function aoMudar(campo: keyof Rascunho) {
    return (event: { target: { value: string } }) => {
      aoDigitar(campo)(event.target.value);
    };
  }

  /** De onde veio o valor na tela, em texto discreto. Em branco nao diz nada. */
  function origem(campo: keyof Rascunho): string | undefined {
    if (valores[campo] === '') return undefined;
    if (campo in editados) return 'Digitado';
    return textoDaOrigem(perfil, campo) || 'Digitado';
  }

  const invalido = camposDeDinheiro.some(
    (campo) => moneyInputError(valores[campo]) !== undefined,
  );

  const salvar = useMutation({
    mutationFn: () =>
      policyholderFile.saveCreditProfile(tomadorId, {
        ...perfil,
        credit_size: valores.credit_size,
        financial_risk_class: valores.financial_risk_class,
        final_score: inteiroOuNulo(valores.final_score),
        risk_score: valores.risk_score,
        presumed_revenue: dinheiroOuNulo(valores.presumed_revenue),
        payment_capacity: dinheiroOuNulo(valores.payment_capacity),
        pefin: dinheiroOuNulo(valores.pefin),
        refin: dinheiroOuNulo(valores.refin),
        protest: dinheiroOuNulo(valores.protest),
        overdue_debt: dinheiroOuNulo(valores.overdue_debt),
        ccf_count: inteiroOuNulo(valores.ccf_count),
        restrictive_points: inteiroOuNulo(valores.restrictive_points),
        restrictive_consulted_on: valores.restrictive_consulted_on || null,
        special_situation: valores.special_situation,
        bureau_name: valores.bureau_name,
        reviewed_payment_capacity: dinheiroOuNulo(valores.reviewed_payment_capacity),
        actual_revenue: dinheiroOuNulo(valores.actual_revenue),
        reviewed_size: valores.reviewed_size,
        reviewed_limit: dinheiroOuNulo(valores.reviewed_limit),
        reviewed_on: valores.reviewed_on || null,
        review_notes: valores.review_notes,
        // A pessoa pode digitar o valor que ja estava: o campo passa a ser dela.
        typed_fields: Object.keys(editados),
      }),
    onSuccess: (gravado) => {
      toast.success('Analise de credito salva.');
      queryClient.setQueryData(['policyholder', tomadorId, 'credit-profile'], gravado);
      setEditados({});
      void queryClient.invalidateQueries({ queryKey: ['policyholder', tomadorId] });
    },
  });
  const apiError = salvar.error instanceof ApiError ? salvar.error : null;

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (!invalido) salvar.mutate();
      }}
    >
      {salvar.error ? (
        <Alert tone="danger" title="Nao foi possivel salvar">
          {salvar.error.message}
        </Alert>
      ) : null}
      {!podeEditar ? (
        <p className="text-2xs text-ink-500">
          Somente quem tem a permissao de alterar o credito do tomador grava a analise
          de credito e os restritivos.
        </p>
      ) : null}
      <fieldset
        disabled={!podeEditar || salvar.isPending}
        className="flex flex-col gap-4"
      >
        <div className="grid gap-4 sm:grid-cols-3">
          <MoneyField
            label="Capacidade de Pagamento (R$)"
            value={valores.reviewed_payment_capacity}
            error={
              moneyInputError(valores.reviewed_payment_capacity) ??
              fieldErrorOf(apiError, 'reviewed_payment_capacity')
            }
            onValueChange={aoDigitar('reviewed_payment_capacity')}
          />
          <MoneyField
            label="Faturamento Real (R$)"
            value={valores.actual_revenue}
            error={
              moneyInputError(valores.actual_revenue) ??
              fieldErrorOf(apiError, 'actual_revenue')
            }
            onValueChange={aoDigitar('actual_revenue')}
          />
          <Field
            label="Porte da analise"
            placeholder="Digite"
            value={valores.reviewed_size}
            hint={origem('reviewed_size')}
            onChange={aoMudar('reviewed_size')}
          />
          <MoneyField
            label="Limite Revisado (R$)"
            value={valores.reviewed_limit}
            error={
              moneyInputError(valores.reviewed_limit) ??
              fieldErrorOf(apiError, 'reviewed_limit')
            }
            onValueChange={aoDigitar('reviewed_limit')}
          />
          <Field
            label="Data da Revisao"
            type="date"
            value={valores.reviewed_on}
            onChange={aoMudar('reviewed_on')}
          />
        </div>
        <Textarea
          label="Observacoes da analise"
          placeholder="Digite aqui"
          value={valores.review_notes}
          onChange={aoMudar('review_notes')}
        />

        <div className="flex flex-col gap-1 border-t border-line pt-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
            Restritivos e risco (preenchidos pela consulta, editaveis)
          </h3>
          <p className="text-2xs text-ink-500">
            Cada campo diz de onde veio. O que uma pessoa digita fica como esta; a
            consulta so atualiza campo em branco ou que ela mesma preencheu.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <Select
            label="Porte de credito"
            placeholder="Nao informado"
            options={portesDeCredito}
            value={valores.credit_size}
            hint={origem('credit_size')}
            error={fieldErrorOf(apiError, 'credit_size')}
            onChange={aoMudar('credit_size')}
          />
          <Select
            label="Classe de Risco Financeiro"
            placeholder="Nao informada"
            options={classesDeRisco}
            value={valores.financial_risk_class}
            hint={origem('financial_risk_class')}
            error={fieldErrorOf(apiError, 'financial_risk_class')}
            onChange={aoMudar('financial_risk_class')}
          />
          <Field
            label="Score Final"
            inputMode="numeric"
            value={valores.final_score}
            hint={origem('final_score')}
            error={fieldErrorOf(apiError, 'final_score')}
            onChange={aoMudar('final_score')}
          />
          <Field
            label="Risco Score"
            value={valores.risk_score}
            hint={origem('risk_score')}
            onChange={aoMudar('risk_score')}
          />
          <MoneyField
            label="Fat. Presumido Anual (R$)"
            value={valores.presumed_revenue}
            hint={origem('presumed_revenue')}
            error={moneyInputError(valores.presumed_revenue)}
            onValueChange={aoDigitar('presumed_revenue')}
          />
          <MoneyField
            label="Capacidade de pagamento do bureau (R$)"
            value={valores.payment_capacity}
            hint={origem('payment_capacity')}
            error={moneyInputError(valores.payment_capacity)}
            onValueChange={aoDigitar('payment_capacity')}
          />
          <MoneyField
            label="Debitos Pefins (R$)"
            value={valores.pefin}
            hint={origem('pefin')}
            error={moneyInputError(valores.pefin)}
            onValueChange={aoDigitar('pefin')}
          />
          <MoneyField
            label="Debitos Refins (R$)"
            value={valores.refin}
            hint={origem('refin')}
            error={moneyInputError(valores.refin)}
            onValueChange={aoDigitar('refin')}
          />
          <MoneyField
            label="Protestos (R$)"
            value={valores.protest}
            hint={origem('protest')}
            error={moneyInputError(valores.protest)}
            onValueChange={aoDigitar('protest')}
          />
          <MoneyField
            label="Dividas Vencidas (R$)"
            value={valores.overdue_debt}
            hint={origem('overdue_debt')}
            error={moneyInputError(valores.overdue_debt)}
            onValueChange={aoDigitar('overdue_debt')}
          />
          <Field
            label="CCF"
            inputMode="numeric"
            value={valores.ccf_count}
            hint={origem('ccf_count')}
            error={fieldErrorOf(apiError, 'ccf_count')}
            onChange={aoMudar('ccf_count')}
          />
          <Field
            label="Pontos"
            inputMode="numeric"
            value={valores.restrictive_points}
            hint={origem('restrictive_points')}
            error={fieldErrorOf(apiError, 'restrictive_points')}
            onChange={aoMudar('restrictive_points')}
          />
          <Field
            label="Data de Consulta Restritivos"
            type="date"
            value={valores.restrictive_consulted_on}
            hint={origem('restrictive_consulted_on')}
            onChange={aoMudar('restrictive_consulted_on')}
          />
          <Select
            label="Situacao especial"
            placeholder="Nao informada"
            options={situacoesEspeciais}
            value={valores.special_situation}
            hint={origem('special_situation')}
            onChange={aoMudar('special_situation')}
          />
          <Field
            label="Bureau"
            value={valores.bureau_name}
            hint={origem('bureau_name')}
            onChange={aoMudar('bureau_name')}
          />
        </div>
      </fieldset>
      {podeEditar ? (
        <div className="flex justify-end">
          <Button type="submit" loading={salvar.isPending} disabled={invalido}>
            Salvar
          </Button>
        </div>
      ) : null}
    </form>
  );
}

// --- Dados Bancarios ------------------------------------------------------------

function DadosBancarios({ tomador }: { tomador: Policyholder }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const podeIncluir = can('tomadores.dados_bancarios.update');
  const podeVerMeios = can('configuracoes.meio_pagamento.view');
  const [incluindo, setIncluindo] = useState(false);
  const [meio, setMeio] = useState('');
  const [conta, setConta] = useState<BankAccount>(emptyBankAccount);

  const contas = useQuery({
    queryKey: ['policyholder', tomador.id, 'bank-accounts'],
    queryFn: ({ signal }) => policyholderFile.bankAccounts(tomador.id, signal),
  });
  // Nome do banco na lista, do catalogo do Banco Central.
  const bancos = useQuery({
    queryKey: ['catalogs', 'bank-codes'],
    queryFn: ({ signal }) => catalogBanks.list(signal),
    staleTime: 60 * 60 * 1000,
  });
  // Meios de pagamento da empresa (Configuracoes), so os ativos.
  const meios = useQuery({
    queryKey: ['payment-methods', 'ativos'],
    queryFn: ({ signal }) => paymentMethods.list({}, signal),
    enabled: incluindo && podeVerMeios,
  });
  const nomeDoBanco = (codigo: string) =>
    bancos.data?.find((banco) => banco.compe_code === codigo)?.name;

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['policyholder', tomador.id] });
  }

  const incluir = useMutation({
    mutationFn: () =>
      policyholderFile.addBankAccount(tomador.id, {
        payment_method: '',
        payment_method_id: meio || null,
        bank_account: conta,
      }),
    onSuccess: () => {
      toast.success('Conta bancaria incluida.');
      setIncluindo(false);
      setConta(emptyBankAccount);
      setMeio('');
      invalidar();
    },
  });
  const remover = useMutation({
    mutationFn: (contaId: string) =>
      policyholderFile.removeBankAccount(tomador.id, contaId),
    onSuccess: () => {
      toast.success('Conta bancaria removida.');
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });
  const apiError = incluir.error instanceof ApiError ? incluir.error : null;

  return (
    <Secao
      titulo="Dados Bancarios"
      descricao="Contas do tomador, destino de devolucao de dinheiro. Incluir conta tem permissao propria, como a conta da corretora."
      carregando={contas.isPending}
      erro={contas.error}
      acao={
        podeIncluir ? (
          <Button size="sm" onClick={() => setIncluindo(true)}>
            Adicionar conta bancaria
          </Button>
        ) : null
      }
    >
      <DataTable
        columns={[
          {
            key: 'forma',
            header: 'Meio de pagamento',
            render: (c) => c.payment_method || '-',
          },
          {
            key: 'banco',
            header: 'Banco',
            render: (c) => {
              const nome = nomeDoBanco(c.bank_account.bank_code);
              return nome
                ? `${c.bank_account.bank_code} - ${nome}`
                : c.bank_account.bank_code;
            },
          },
          {
            key: 'tipo',
            header: 'Tipo',
            render: (c) =>
              c.bank_account.account_type === 'poupanca' ? 'Poupanca' : 'Corrente',
          },
          {
            key: 'agencia',
            header: 'Agencia',
            render: (c) =>
              [c.bank_account.agency, c.bank_account.agency_digit]
                .filter(Boolean)
                .join('-'),
          },
          {
            key: 'conta',
            header: 'Conta',
            render: (c) =>
              [c.bank_account.account, c.bank_account.account_digit]
                .filter(Boolean)
                .join('-'),
          },
          {
            key: 'quem',
            header: 'Incluida por',
            render: (c) => c.created_label || '-',
          },
          {
            key: 'acoes',
            header: 'Acoes',
            render: (c) =>
              podeIncluir ? (
                <AcaoComIcone
                  acao="remover"
                  rotulo="Remover"
                  onClick={() => remover.mutate(c.id)}
                />
              ) : null,
          },
        ]}
        items={contas.data ?? []}
        keyOf={(c) => c.id}
        empty={<EmptyState icon="bank" title="Ops! Nenhuma conta encontrada!" />}
      />
      <Modal
        open={incluindo}
        onClose={() => setIncluindo(false)}
        title="Adicionar conta bancaria"
        size="md"
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            incluir.mutate();
          }}
        >
          {incluir.error ? (
            <Alert tone="danger" title="Nao foi possivel incluir">
              {incluir.error.message}
            </Alert>
          ) : null}
          <Select
            label="Meio de pagamento"
            placeholder="Selecione o meio de pagamento"
            options={(meios.data ?? []).map((item) => ({
              value: item.id,
              label: item.label,
            }))}
            value={meio}
            disabled={(meios.data ?? []).length === 0}
            error={fieldErrorOf(apiError, 'payment_method_id')}
            hint={
              !podeVerMeios
                ? 'Sem a permissao de ler os meios de pagamento da empresa.'
                : meios.isSuccess && meios.data.length === 0
                  ? 'Nenhum meio de pagamento cadastrado. Cadastre em Configuracoes, Meios de pagamento.'
                  : undefined
            }
            onChange={(event) => setMeio(event.target.value)}
          />
          <FinancialBankAccountFields
            value={conta}
            onChange={setConta}
            apiError={apiError}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setIncluindo(false)}>
              Cancelar
            </Button>
            <Button type="submit" loading={incluir.isPending}>
              Salvar
            </Button>
          </div>
        </form>
      </Modal>
    </Secao>
  );
}

// --- Observacoes ----------------------------------------------------------------

function Observacoes({ tomador }: { tomador: Policyholder }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const podeIncluir = can('tomadores.ficha.update');
  const [busca, setBusca] = useState('');
  const [texto, setTexto] = useState('');
  const [incluindo, setIncluindo] = useState(false);

  const observacoes = useQuery({
    queryKey: ['policyholder', tomador.id, 'observations', busca],
    queryFn: ({ signal }) => policyholderFile.observations(tomador.id, busca, signal),
  });
  const incluir = useMutation({
    mutationFn: () => policyholderFile.addObservation(tomador.id, texto),
    onSuccess: () => {
      toast.success('Observacao registrada.');
      setTexto('');
      setIncluindo(false);
      void queryClient.invalidateQueries({ queryKey: ['policyholder', tomador.id] });
    },
  });

  return (
    <Secao
      titulo="Historico de observacoes"
      descricao="Observacao nao se edita nem se apaga: o historico guarda o que foi registrado."
      erro={observacoes.error}
      acao={
        podeIncluir ? (
          <Button size="sm" onClick={() => setIncluindo(true)}>
            Adicionar observacao
          </Button>
        ) : null
      }
    >
      <Field
        label="Buscar"
        placeholder="Buscar"
        value={busca}
        onChange={(event) => setBusca(event.target.value)}
      />
      {(observacoes.data ?? []).length === 0 && !observacoes.isPending ? (
        <EmptyState icon="file" title="Ops! Nenhuma observacao registrada" />
      ) : (
        <ul className="flex flex-col gap-3">
          {(observacoes.data ?? []).map((item) => (
            <li key={item.id} className="rounded-md border border-line p-3">
              <p className="whitespace-pre-wrap text-sm text-ink-800">{item.body}</p>
              <p className="mt-1 text-2xs text-ink-500">
                {item.author_label || 'sistema'} em {formatDateTime(item.created_at)}
              </p>
            </li>
          ))}
        </ul>
      )}
      <Modal
        open={incluindo}
        onClose={() => setIncluindo(false)}
        title="Adicionar observacao"
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIncluindo(false)}>
              Cancelar
            </Button>
            <Button
              loading={incluir.isPending}
              disabled={texto.trim() === ''}
              onClick={() => incluir.mutate()}
            >
              Salvar
            </Button>
          </>
        }
      >
        {incluir.error ? (
          <Alert tone="danger" title="Nao foi possivel registrar">
            {incluir.error.message}
          </Alert>
        ) : null}
        <Textarea
          label="Observacao"
          value={texto}
          onChange={(event) => setTexto(event.target.value)}
        />
      </Modal>
    </Secao>
  );
}
